import { type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuthState } from '@/components/feature/AuthGuard';
import { useQiRole } from '../lib/useQiRole';
import type { QiRole } from '../lib/types';

export interface QiAuth {
  userId: string;
  email: string | undefined;
  role: QiRole;
  signOut: () => Promise<void>;
}

export default function AuthGate({
  children,
  requireStaff = false,
  requirePractitioner = false,
}: {
  children: (auth: QiAuth) => ReactNode;
  requireStaff?: boolean;
  requirePractitioner?: boolean;
}) {
  const auth = useAuthState();
  const location = useLocation();
  const { role, loading: roleLoading } = useQiRole(auth.user);

  if (auth.loading || (auth.user && roleLoading)) {
    return (
      <div className="py-20 text-center space-y-3">
        <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
        <p className="text-sm text-foreground-500">載入中……</p>
      </div>
    );
  }

  if (!auth.user) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/account?returnTo=${returnTo}`} replace />;
  }

  if (requireStaff && role !== 'admin' && role !== 'reviewer') {
    return (
      <QiAccessDenied
        icon="ri-shield-user-line"
        title="此頁面需要覆核者或管理員權限"
        description="覆核後台僅開放給 ABAA 及指定 Reviewer。你目前沒有此權限。"
      />
    );
  }

  if (requirePractitioner && role !== 'practitioner' && role !== 'candidate') {
    return (
      <QiAccessDenied
        icon="ri-user-add-line"
        title="此頁面需要施炁者培育資格"
        description="只有準施炁者（實作培育中）或正式授權施炁者才能建立宇炁案例。若你尚未取得培育資格，請聯繫管理員開通。"
      />
    );
  }

  return (
    <>{children({ userId: auth.user.id, email: auth.user.email, role, signOut: auth.signOut })}</>
  );
}

function QiAccessDenied({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="py-16 text-center space-y-4">
      <div className="w-16 h-16 mx-auto rounded-full bg-foreground-100 flex items-center justify-center">
        <i className={`${icon} text-foreground-400 text-2xl`} />
      </div>
      <h2 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
        {title}
      </h2>
      <p className="text-sm text-foreground-500 max-w-sm mx-auto">{description}</p>
    </div>
  );
}

export function QiAuthForm() {
  const location = useLocation();
  const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);

  return (
    <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 md:p-8 max-w-md mx-auto space-y-5 text-center">
      <span className="inline-flex w-11 h-11 rounded-full bg-accent-500 items-center justify-center">
        <i className="ri-flashlight-line text-xl text-background-50" />
      </span>
      <div>
        <h2 className="text-lg text-foreground-800 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
          登入 AUM1001
        </h2>
        <p className="text-xs text-foreground-500 leading-relaxed">
          宇炁療育使用全站統一的 AUM1001 帳號，與 RG、OG、ADM 管理共用同一組登入憑證。
        </p>
      </div>
      <Link
        to={`/account?returnTo=${returnTo}`}
        className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-6 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer w-full"
      >
        <i className="ri-login-box-line text-lg" />
        前往 /account 登入
      </Link>
      <p className="text-xs text-foreground-400">
        尚未建立 AUM1001 帳號？請至 /account 完成註冊與 Email 驗證。
      </p>
    </div>
  );
}